import { useState } from 'react';

import { ReactComponent as SortMenuAscIcon } from '../../icons/sort-menu-asc.svg';

import Label from '../../form/Label';
import RadioGroupItem, { RadioGroup } from '../../form/Radio';
import cn from '../../utils/cn';
import Button from '../Button';
import Dropdown, { type Placement } from '../Dropdown';
import Typography from '../Typography/Typography';

export interface Option<T> {
  value: T;
  key: string;
}

export interface SortProps<T> {
  buttonTitle?: string;
  title?: string;
  options: Array<Option<T>>;
  defaultOption?: Option<T>;
  onChange: (option: Option<T>) => void;
  placement?: Placement;
  className?: string;
}

export default function Sort<T>({ buttonTitle, title, options, defaultOption, onChange, placement, className }: SortProps<T>) {
  const [selected, setSelected] = useState<Option<T> | undefined>(defaultOption);
  const [firstTime, setFirstTime] = useState(true);

  const handleChange = (value: string) => {
    const selectedByKey: Option<T> | undefined = options.find((option) => option.key === value);

    if (selectedByKey) {
      setSelected(selectedByKey);
      setFirstTime(false);
      onChange(selectedByKey);
    }
  };

  const getButtonTitle = () => {
    if (!buttonTitle) return null;

    if (firstTime) {
      return buttonTitle;
    }
    if (selected) {
      return selected.key;
    }
  };

  return (
    <>
      <Dropdown
        placement={placement}
        overlay={
          <Dropdown.Overlay className={cn('w-full', className)}>
            {title && (
              <Dropdown.Header className='px-2'>
                <Typography variant='description-title' className='text-gray-dark'>
                  {title}
                </Typography>
              </Dropdown.Header>
            )}
            <Dropdown.Body className='px-2 mt-0.5'>
              <RadioGroup defaultValue={defaultOption?.key} onValueChange={(value) => handleChange(value)}>
                {options.map((option) => (
                  <div key={option.key} className='flex items-center space-x-2'>
                    <RadioGroupItem id={option.key} value={option.key} />
                    <Label htmlFor={option.key}>{option.key}</Label>
                  </div>
                ))}
              </RadioGroup>
            </Dropdown.Body>
          </Dropdown.Overlay>
        }
      >
        <Button variant='text' size='condensed'>
          <SortMenuAscIcon className='size-4 mr-2' />
          {getButtonTitle()}
        </Button>
      </Dropdown>
    </>
  );
}
